import { useState, useEffect } from 'react';
import { useParams, useOutletContext, Navigate, useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { ArrowLeft, Download, Save, ChevronLeft, ChevronRight, MapPin, ExternalLink } from 'lucide-react';
import Logo from '../../components/Logo';

interface LayoutContext {
  viewAsStoreId?: string;
  viewAsStoreName?: string;
}

interface QRCodeData {
  id: string;
  code: string;
  url: string;
  qrImagePng: string;
  bannerText: string | null;
  bannerTextColor: string | null;
  showStoreLogo: boolean;
  store: {
    id: string;
    name: string;
    logoUrl: string | null;
    primaryColor: string | null;
    secondaryColor: string | null;
  };
  run: {
    id: string;
    instanceNumber: number;
    startDate: string;
    endDate: string;
    campaign: {
      id: string;
      name: string;
      description: string | null;
      headerColor: string | null;
    };
  };
}

export default function QRCodeDesignerPage() {
  const { qrcodeId } = useParams<{ qrcodeId: string }>();
  const { viewAsStoreId, viewAsStoreName } = useOutletContext<LayoutContext>() || {};
  const navigate = useNavigate();

  const [qrCode, setQRCode] = useState<QRCodeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Editable fields
  const [bannerText, setBannerText] = useState('');
  const [bannerTextColor, setBannerTextColor] = useState('#FFFFFF');
  const [showStoreLogo, setShowStoreLogo] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [phonePreviewView, setPhonePreviewView] = useState<'form' | 'success'>('form');

  // Redirect if not viewing as a store
  if (!viewAsStoreId) {
    return <Navigate to="/admin/stores" replace />;
  }

  useEffect(() => {
    if (qrcodeId) {
      loadQRCode();
    }
  }, [qrcodeId, viewAsStoreId]);

  useEffect(() => {
    if (qrCode) {
      const textChanged = bannerText !== (qrCode.bannerText || '');
      const colorChanged = bannerTextColor !== (qrCode.bannerTextColor || '#FFFFFF');
      const logoChanged = showStoreLogo !== qrCode.showStoreLogo;
      setHasChanges(textChanged || colorChanged || logoChanged);
    }
  }, [bannerText, bannerTextColor, showStoreLogo, qrCode]);

  const loadQRCode = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: QRCodeData }>(
        `/admin/stores/${viewAsStoreId}/qrcodes/${qrcodeId}`
      );
      const data = response.data.data;
      setQRCode(data);
      setBannerText(data.bannerText || '');
      setBannerTextColor(data.bannerTextColor || '#FFFFFF');
      setShowStoreLogo(data.showStoreLogo);
    } catch (error) {
      console.error('Failed to load QR code:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!qrCode) return;

    setSaving(true);
    try {
      const response = await apiClient.put<{ success: boolean; data: QRCodeData }>(
        `/admin/stores/${viewAsStoreId}/qrcodes/${qrCode.id}`,
        { bannerText: bannerText || null, bannerTextColor, showStoreLogo }
      );
      setQRCode(response.data.data);
      setMessage({ type: 'success', text: 'Design saved successfully' });
      setHasChanges(false);
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to save design' });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!qrCode) return;

    try {
      const blob = await apiClient.getBlob(`/admin/stores/${viewAsStoreId}/qrcodes/${qrCode.id}/pdf`);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${qrCode.store.name}_${qrCode.run.campaign.name}_QR.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    }
  };

  const goBack = () => {
    navigate(`/admin/qrcodes?viewAs=${viewAsStoreId}`);
  };

  if (loading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  if (!qrCode) {
    return <div className="text-red-600">QR code not found</div>;
  }

  const primaryColor = qrCode.store.primaryColor || '#000000';
  const secondaryColor = qrCode.store.secondaryColor || '#FFFFFF';

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <button onClick={goBack} className="text-gray-500 hover:text-gray-700">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">QR Code Designer</h1>
            <p className="text-sm text-gray-500">{qrCode.run.campaign.name} - Run #{qrCode.run.instanceNumber}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
          <button
            onClick={handleDownloadPDF}
            className="btn-secondary flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
        </div>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Preview */}
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Flyer Preview</h2>
            <p className="text-sm text-gray-500 mb-4">This is how your QR code flyer will look when printed.</p>

          {/* Flyer Preview - 8.5" x 11" aspect ratio */}
          <div
            className="bg-white border-2 border-gray-200 rounded-lg shadow-lg mx-auto flex flex-col"
            style={{ width: '100%', maxWidth: '400px', aspectRatio: '8.5 / 11' }}
          >
            {/* Header - Discover The Park (non-editable, uses campaign color) */}
            <div
              className="text-white py-3 px-4 flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: qrCode.run.campaign.headerColor || '#0891b2' }}
            >
              <div className="flex items-center gap-2">
                <Logo size="sm" />
                <h3 className="font-bold text-base">Discover The Park</h3>
              </div>
            </div>

            {/* Campaign Name (non-editable) */}
            <div className="text-center py-4 px-4 flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-900">{qrCode.run.campaign.name}</h2>
              {qrCode.run.campaign.description && (
                <p className="text-gray-600 mt-1 text-xs">{qrCode.run.campaign.description}</p>
              )}
              <p className="text-gray-500 mt-1 text-xs">Winners will be notified by email</p>
            </div>

            {/* QR Code (non-editable) */}
            <div className="flex-1 flex flex-col items-center justify-center py-2">
              <div className="bg-white p-3 rounded-lg shadow-inner border border-gray-100">
                {qrCode.qrImagePng ? (
                  <img
                    src={qrCode.qrImagePng}
                    alt="QR Code"
                    className="w-36 h-36"
                  />
                ) : (
                  <div className="w-36 h-36 bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                    QR Code
                  </div>
                )}
              </div>
              <p className="text-center text-gray-600 font-medium text-sm mt-3">
                Scan to Enter!
              </p>
              <p className="text-center text-gray-400 text-xs mt-1">
                Point your phone camera at the QR code
              </p>
            </div>

            {/* Store Banner (editable) */}
            <div
              className="py-4 px-4 flex items-center justify-between flex-shrink-0"
              style={{ backgroundColor: secondaryColor }}
            >
              <div className="flex-1">
                {bannerText ? (
                  <p className="font-semibold text-sm" style={{ color: bannerTextColor }}>
                    {bannerText}
                  </p>
                ) : (
                  <p className="font-semibold text-sm" style={{ color: primaryColor }}>
                    {qrCode.store.name}
                  </p>
                )}
              </div>
              {showStoreLogo && qrCode.store.logoUrl && (
                <img
                  src={qrCode.store.logoUrl}
                  alt={qrCode.store.name}
                  className="w-10 h-10 object-contain"
                />
              )}
            </div>
          </div>
          </div>

          {/* Phone Preview - What customers see */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer Phone Preview</h2>
            <p className="text-sm text-gray-500 mb-4">This is what customers see when they scan the QR code.</p>

            {/* Toggle Buttons */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <button
                onClick={() => setPhonePreviewView('form')}
                disabled={phonePreviewView === 'form'}
                className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm font-medium text-gray-700 min-w-[120px] text-center">
                {phonePreviewView === 'form' ? 'Registration Form' : 'Success Page'}
              </span>
              <button
                onClick={() => setPhonePreviewView('success')}
                disabled={phonePreviewView === 'success'}
                className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Phone Frame */}
            <div className="mx-auto w-[280px] h-[560px] bg-gray-900 rounded-[3rem] p-3 shadow-xl">
              <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-900 rounded-b-xl z-10"></div>

                {/* Screen Content */}
                <div className="w-full h-full overflow-y-auto">
                  {phonePreviewView === 'form' ? (
                    /* Form View */
                    <div className="min-h-full" style={{ backgroundColor: qrCode.store.secondaryColor || '#f9fafb' }}>
                      {/* Header */}
                      <div
                        className="p-4 pt-8 text-center"
                        style={{ backgroundColor: qrCode.store.primaryColor || '#22c55e' }}
                      >
                        {qrCode.store.logoUrl && (
                          <img
                            src={qrCode.store.logoUrl}
                            alt={qrCode.store.name}
                            className="h-10 mx-auto mb-2"
                          />
                        )}
                        <h1 className="text-lg font-bold text-white">{qrCode.store.name}</h1>
                      </div>

                      {/* Campaign info */}
                      <div className="p-4 text-center">
                        <h2 className="text-base font-bold text-gray-900 mb-1">{qrCode.run.campaign.name}</h2>
                        {qrCode.run.campaign.description && (
                          <p className="text-xs text-gray-600">{qrCode.run.campaign.description}</p>
                        )}
                      </div>

                      {/* Form */}
                      <div className="px-4 pb-4">
                        <div className="bg-white rounded-xl shadow-lg p-4">
                          <h3 className="text-sm font-bold text-gray-900 mb-3 text-center">
                            Enter the Raffle!
                          </h3>
                          <div className="space-y-3">
                            <div>
                              <label className="text-xs text-gray-600">Your Name</label>
                              <div className="mt-1 h-8 bg-gray-100 rounded border border-gray-200"></div>
                            </div>
                            <div>
                              <label className="text-xs text-gray-600">Email Address</label>
                              <div className="mt-1 h-8 bg-gray-100 rounded border border-gray-200"></div>
                            </div>
                            <div
                              className="h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold"
                              style={{ backgroundColor: qrCode.store.primaryColor || '#22c55e' }}
                            >
                              Enter Raffle
                            </div>
                          </div>
                          <p className="text-[10px] text-gray-400 text-center mt-3">
                            Each scan = 1 raffle entry. Visit more stores for more chances to win!
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Success View */
                    <div
                      className="min-h-full flex flex-col items-center justify-center p-4 pt-8"
                      style={{ backgroundColor: qrCode.store.primaryColor || '#22c55e' }}
                    >
                      {/* Header */}
                      <div className="flex items-center gap-2 mb-4">
                        <Logo size="sm" className="drop-shadow-lg" />
                        <div className="text-white">
                          <h2 className="text-sm font-bold leading-tight">Discover</h2>
                          <h2 className="text-sm font-bold leading-tight">the Park</h2>
                        </div>
                      </div>

                      {/* Campaign Badge */}
                      <div className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 mb-4">
                        <span className="text-white text-xs font-medium">{qrCode.run.campaign.name}</span>
                      </div>

                      <div className="text-center text-white">
                        <div className="text-4xl mb-2">🎉</div>
                        <h1 className="text-xl font-bold mb-2">You're In!</h1>
                        <p className="text-sm mb-2">
                          You now have <span className="font-bold text-2xl">3</span> entries in the raffle!
                        </p>
                        <p className="text-white/90 text-xs mb-4">
                          You will be contacted by email if you won a prize on or about {qrCode?.run?.endDate ? new Date(qrCode.run.endDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : '[End Date]'}.
                        </p>

                        {/* Mini Map Preview */}
                        <div className="mt-4 pt-4 border-t border-white/20">
                          <p className="text-white/90 text-xs mb-3">
                            Visit other stores for more chances to win!
                          </p>
                          <div className="bg-white rounded-lg p-2 shadow-lg">
                            <div className="bg-gray-200 rounded h-24 flex items-center justify-center">
                              <div className="text-center">
                                <MapPin className="w-6 h-6 text-primary-600 mx-auto" />
                                <p className="text-[10px] text-gray-600 mt-1">View Map</p>
                              </div>
                            </div>
                          </div>
                          <button className="mt-3 inline-flex items-center gap-1 px-3 py-2 bg-white text-gray-900 rounded-lg text-xs font-semibold">
                            <MapPin className="w-3 h-3" />
                            View Other Stores
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customization Options */}
        <div className="space-y-6">
          {/* Banner Text */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Banner Customization</h2>
            <p className="text-sm text-gray-500 mb-4">
              Customize the banner at the bottom of your QR code flyer.
            </p>

            <div className="space-y-4">
              <div>
                <label className="label">Banner Text</label>
                <input
                  type="text"
                  value={bannerText}
                  onChange={(e) => setBannerText(e.target.value)}
                  className="input"
                  placeholder={qrCode.store.name}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to show your store name
                </p>
              </div>

              <div>
                <label className="label">Text Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={bannerTextColor}
                    onChange={(e) => setBannerTextColor(e.target.value)}
                    className="w-12 h-12 rounded-lg border border-gray-200 cursor-pointer p-1"
                  />
                  <input
                    type="text"
                    value={bannerTextColor}
                    onChange={(e) => setBannerTextColor(e.target.value)}
                    className="input w-28 font-mono text-sm"
                    placeholder="#FFFFFF"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="showStoreLogo"
                  checked={showStoreLogo}
                  onChange={(e) => setShowStoreLogo(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="showStoreLogo" className="text-sm text-gray-700">
                  Show store logo on banner
                </label>
              </div>
            </div>
          </div>

          {/* Store Colors Info */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Store Colors</h2>
            <p className="text-sm text-gray-500 mb-4">
              The banner background uses your store's secondary color. Update these in Store Settings.
            </p>

            <div className="flex gap-6">
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-2">Primary</label>
                <div className="flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded-lg border border-gray-200"
                    style={{ backgroundColor: primaryColor }}
                  />
                  <span className="text-sm font-mono text-gray-600">{primaryColor}</span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-2">Secondary (Banner BG)</label>
                <div className="flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded-lg border border-gray-200"
                    style={{ backgroundColor: secondaryColor }}
                  />
                  <span className="text-sm font-mono text-gray-600">{secondaryColor}</span>
                </div>
              </div>
            </div>

            <a
              href={`/admin/store-settings?viewAs=${viewAsStoreId}`}
              className="text-primary-600 hover:text-primary-700 text-sm mt-4 inline-block"
            >
              Edit store colors
            </a>
          </div>

          {/* Tips */}
          <div className="card bg-blue-50 border-blue-100">
            <h3 className="font-semibold text-blue-900 mb-2">Tips for Printing</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>Print on standard letter size paper (8.5" x 11")</li>
              <li>Use color printing for best results</li>
              <li>Place near checkout or store entrance</li>
              <li>Consider laminating for durability</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
